const SECRET_SALT = process.env.REACT_APP_SECRET_SALT;
let decryptedRoleCache = {
  roleId: "",
  roleName: "",
};

export const setDecryptedRoleCache = (roleId = "", roleName = "") => {
  decryptedRoleCache = {
    roleId,
    roleName,
  };
};

/**
 * PHP encryptId() stores IV + TAG + CIPHERTEXT as base64url.
 * Web Crypto expects CIPHERTEXT + TAG for AES-GCM decrypt.
 */
const base64UrlToBytes = (value) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const bytesToBase64Url = (bytes) => {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const getAesGcmKey = async () => {
  const secretBytes = new TextEncoder().encode(SECRET_SALT);
  const keyBytes = await crypto.subtle.digest("SHA-256", secretBytes);

  return crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
};

export const decryptData = async (encryptedData) => {
  if (!encryptedData) return '';

  try {
    const payload = base64UrlToBytes(encryptedData);

    if (payload.length < 29) {
      return '';
    }

    const iv = payload.slice(0, 12);
    const tag = payload.slice(12, 28);
    const ciphertext = payload.slice(28);
    const encryptedPayload = new Uint8Array(ciphertext.length + tag.length);

    encryptedPayload.set(ciphertext);
    encryptedPayload.set(tag, ciphertext.length);

    const key = await getAesGcmKey();
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      encryptedPayload,
    );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    return '';
  }
};

/**
 * Encrypt data using the same format as Laravel encryptId().
 */
export const encryptData = async (data) => {
  if (!data) return '';

  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await getAesGcmKey();
    const encrypted = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        key,
        new TextEncoder().encode(String(data)),
      ),
    );
    const ciphertext = encrypted.slice(0, -16);
    const tag = encrypted.slice(-16);
    const payload = new Uint8Array(iv.length + tag.length + ciphertext.length);

    payload.set(iv);
    payload.set(tag, iv.length);
    payload.set(ciphertext, iv.length + tag.length);

    return bytesToBase64Url(payload);
  } catch (error) {
    return '';
  }
};

export const getDecryptedRoleId = () => {
  return decryptedRoleCache.roleId || '';
};

export const getDecryptedRoleName = () => {
  return decryptedRoleCache.roleName || '';
};

export const validateEmailAddress = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};
export const maskEmail = (email, length) => {
  if (!email) return "";
  // Split the email address into local part and domain
  const [localPart, domain] = email.split("@");

  // Get the length of the local part
  const localPartLength = localPart.length;

  // Mask the email address
  const maskedEmail =
    localPartLength > 2
      ? `${localPart.substring(0, 2)}${"•".repeat(
        localPartLength - 2 > 10 ? 10 : localPartLength - 2,
      )}@${domain}`
      : `${localPart.substring(0, 2)}${"•".repeat(3)}@${domain}`;

  if (length) return maskedEmail.substring(0, length + 5) + "...";
  if (maskedEmail.length > 15) return maskedEmail.substring(0, 6) + domain;
  return maskedEmail;
};

export const getFormatedDate = (inputDateString, monthType = "short") => {
  // Check if input is null, undefined, or empty
  if (!inputDateString) {
    return { date: null, time: null };
  }
  
  const inputDate = new Date(inputDateString);
  
  // Check if the date is invalid
  if (isNaN(inputDate.getTime())) {
    return { date: null, time: null };
  }

  const formattedDateTime = {
    date: inputDate.toLocaleDateString("en-US", {
      month: monthType,
      day: "2-digit",
      year: "numeric",
    }),
    time: inputDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
  return formattedDateTime;
};

// export const formatPhoneNumber = (phoneNumber, countryCode) => {
//   const parsedNumber = parsePhoneNumberFromString(phoneNumber, countryCode);

//   if (parsedNumber) {
//     // Format the number according to the country-specific format
//     return parsedNumber.formatInternational();
//   } else {
//     // Return the original number if parsing fails
//     return phoneNumber;
//   }
// };
export const formatPhoneNumber = (phoneNumber, countryCode) => {
  const numericPhoneNumber = phoneNumber.replace(/\D/g, "");
  if (phoneNumber.length > 0) {
    switch (countryCode) {
      case "US":
        return `+1 (${numericPhoneNumber.slice(
          1,
          4,
        )}) ${numericPhoneNumber.slice(4, 7)}-${numericPhoneNumber.slice(7)}`;
      case "PK":
        return `+92 ${numericPhoneNumber.slice(
          2,
          5,
        )} ${numericPhoneNumber.slice(5)}`;
      case "GB":
        return `+44 (0) ${numericPhoneNumber.slice(
          1,
          6,
        )} ${numericPhoneNumber.slice(6)}`;
      case "AU":
        return `0${numericPhoneNumber.slice(1, 4)} ${numericPhoneNumber.slice(
          4,
        )}`;
      case "IN":
        return `+91 ${numericPhoneNumber.slice(
          1,
          6,
        )}-${numericPhoneNumber.slice(6)}`;
      case "CA":
        return `+1 (${numericPhoneNumber.slice(
          1,
          4,
        )}) ${numericPhoneNumber.slice(4, 7)}-${numericPhoneNumber.slice(7)}`;
      case "DE":
        return `+49 (0) ${numericPhoneNumber.slice(
          1,
          5,
        )} ${numericPhoneNumber.slice(5, 9)}-${numericPhoneNumber.slice(9)}`;
      case "JP":
        return `0${numericPhoneNumber.slice(1, 3)}-${numericPhoneNumber.slice(
          3,
          7,
        )}-${numericPhoneNumber.slice(7)}`;
      default:
        return phoneNumber;
    }
  } else {
    return phoneNumber;
  }
};

// // Example usage
// const phoneNumber = "+1234567890";
// const countryCode = "US"; // Country code for the United States
// const formattedPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);

export const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.charAt(0)?.toUpperCase() + word.slice(1);
    })
    .join(" ")
    .split("-")
    .map(function (word) {
      return word.charAt(0)?.toUpperCase() + word.slice(1);
    })
    .join("-");
};

export const phoneNumberFormat = (phoneNumber) => {
  if (!phoneNumber.length) return "";
  // Remove all white spaces, parentheses, and dashes
  const cleanedNumber = phoneNumber.replace(/[\s()+-]/g, "");

  // Format the phone number
  const countryCode = cleanedNumber.substring(0, cleanedNumber.length - 10);
  const areaCode = cleanedNumber.substring(
    cleanedNumber.length - 10,
    cleanedNumber.length - 7,
  );
  const firstThree = cleanedNumber.substring(
    cleanedNumber.length - 7,
    cleanedNumber.length - 4,
  );
  const lastFour = cleanedNumber.substring(cleanedNumber.length - 4);

  return `+${countryCode} (${areaCode}) ${firstThree}-${lastFour}`;
};

export const getBadgeVariant = (input) => {
  switch (input) {
    case "Draft":
      return "gray";
    case "New Request":
      return "primary";
    case "New":
      return "primary";
    case "Signed Off":
      return "warning";
    case "Cancelled":
      return "danger";
    case "Completed":
      return "success";
    case "Pending Stakeholder Sign-off":
      return "warning";
    case "Stakeholder Approved":
      return "success";
    case 1:
      return "gray";
    case 2:
      return "primary";
    case 3:
      return "primary";
    case 4:
      return "warning";
    case 5:
      return "danger";
    case 6:
      return "success";
    case 7:
      return "warning";
    case 8:
      return "success";
    default:
      return "blue";
  }
};

export const getFormattedDate = (isoString) => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.?([a-zA-Z]{2})$/;
// export const emailAdressValidator = (email) =>
//   aplhaRegex.test(email.split("@")[0]) && emailRegex.test(email);
export const emailAdressValidator = (email) => {
  if (!email.includes("@")) return false;
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email);
};

const validUrlTlds = new Set(
  [
    "com",
    "org",
    "net",
    "edu",
    "gov",
    "mil",
    "int",
    "co",
    "io",
    "ai",
    "app",
    "dev",
    "info",
    "biz",
    "me",
    "us",
    "uk",
    "ca",
    "au",
    "de",
    "fr",
    "jp",
    "in",
    "pk",
    "cn",
    "br",
    "mx",
    "it",
    "es",
    "nl",
    "se",
    "no",
    "ch",
    "za",
    "ae",
    "sa",
    "sg",
    "hk",
    "nz",
    "ie",
    "tv",
    "cc",
    "xyz",
    "online",
    "site",
    "store",
    "shop",
    "tech",
    "cloud",
    "digital",
    "software",
    "systems",
    "solutions",
    "services",
    "company",
    "inc",
    "llc",
    "bank",
    "finance",
    "financial",
    "capital",
    "credit",
    "cards",
    "pay",
    "global",
  ],
);

export const isValidUrl = (url) => {
  // If empty or only whitespace, return true (handled by optional validation)
  if (!url || url.trim() === '') return true;

  let urlToValidate = url.trim();

  // If URL doesn't have a protocol, add https://
  if (!urlToValidate.match(/^[a-zA-Z]+:\/\//)) {
    urlToValidate = 'https://' + urlToValidate;
  }

  // Validate using URL constructor
  try {
    const urlObj = new URL(urlToValidate);
    const hostname = urlObj.hostname;

    if (!["http:", "https:"].includes(urlObj.protocol)) return false;

    // Special case for localhost
    if (hostname === 'localhost') return true;

    // Split hostname into parts
    const parts = hostname.split('.');

    // Must have at least 2 parts (domain.tld)
    if (parts.length < 2) return false;

    // Get the TLD (last part)
    const tld = parts[parts.length - 1];

    // TLD must be at least 2 characters and contain only letters
    if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) return false;

    // Reject parseable but invalid-looking domains such as "www.dfsf".
    if (!validUrlTlds.has(tld.toLowerCase())) return false;

    // All parts should not be empty
    if (parts.some(part => !part || part.length === 0)) return false;

    return true;
  } catch {
    return false;
  }
};


export const ROLE_LABELS = {
  SUPER_ADMIN: '1',
  ADMIN: '2',
  SALES: '3',
  MANAGER: '4',
  CLIENT: '5',
  VIEW_ONLY: '6',
  STAKEHOLDER: '7',
};
